import { useRef, useState } from 'react'
import { useMutation } from 'react-query'
import { AI, ArrowRight, Edit } from 'almanac-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { Modal } from 'components/Wizards/Wizard/Modal'
import { Input } from 'almanac-ui/TextInputField/Input'
import { AiErrorModal } from 'components/AiErrorModal/AiErrorModal'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { Radio, RadioGroup } from 'components/RadioGroup'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ourUuid } from 'utils'
import { reportError } from 'utils/error'
import { Field } from '../Field/Field'
import { Form } from '../Form/Form'
import { OutlineEditor } from './OutlineEditor/OutlineEditor'
import { OutlineEditorHandle } from './OutlineEditor/Types'
import { generateIntroduction } from '../generateIntroduction'
import { generateOutline } from '../generateOutline'
import { generateTitles } from '../generateTitles'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { OutlineLoadingState } from 'components/Wizards/OutlineLoadingState/OutlineLoadingState'
import { StepOne, Values } from './StepOne'
import { TitlesLoadingState } from './TitlesLoadingState'
import { BlogPostLength, BlogPostWizardProps } from './Types'
import Styles from './BlogPostWizard.module.scss'
import { useSourceMaterials } from 'hooks/useSourceMaterials'
import { useGenerateContent } from 'components/Wizards/Wizard/useGenerateContent'
import { AlmanacFile, GeneratePrompt } from 'types'
import { useRootStore } from 'stores/RootStore'
import { CTA } from 'stores/OnboardingStore'
import { Header } from '../Wizard/Header/Header'
import { Blog } from 'almanac-ui/icons/blaze/32'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from '../Wizard/Header/MobileHeader'
import { WizardProvider } from 'components/Wizards/Wizard/WizardContext'

type Uuid = string
type Identifiable<T> = [Uuid, T]

const BlogPostWizard = ({
  initialValues = {},
  onSuccess,
  nodeSelection,
  articleId,
  layerId,
  seasonalPromotion,
  isTurnInto,
  children,
  schema,
}: BlogPostWizardProps) => {
  const [currentStep, setCurrentStep] = useState(1)
  const workspaceId = useCurrentWorkspaceId()
  const { openModal, closeModal } = useModals()
  const { isTabletPortrait, isMobile } = useResponsiveDesign()
  const onboarding = useRootStore().onboardingStore

  const [formValues, setFormValues] = useState<Values>({
    topic: initialValues.topic || '',
    goal: initialValues.goal || 'promotional',
    audience: initialValues.audience || '',
    blog_length: initialValues.blog_length || BlogPostLength.Medium,
    seo_words: initialValues.seo_words || [],
    call_to_action: initialValues.call_to_action || '',
    author_persona: initialValues.author_persona || '',
    product_description: initialValues.product_description || '',
    avoid: initialValues.avoid || '',
    additional_info: initialValues.additional_info || '',
    tone: initialValues.tone || [],
    brand_voice_id: initialValues.brand_voice_id,
    include_images: initialValues.include_images || false,
    promotion: initialValues.promotion || '',
    language: initialValues.language || '',
    project: initialValues.project,
    filename: '',
  })
  const [introduction, setIntroduction] = useState(initialValues.introduction || '')
  const [outline, setOutline] = useState(initialValues.outline || '')
  const [titleOptions, setTitleOptions] = useState<Array<Identifiable<string>>>([])
  const [selectedTitleId, setSelectedTitleId] = useState<Uuid>()
  const title = titleOptions.find(([uuid]) => uuid === selectedTitleId)?.[1] || ''

  const generateTitlesMutationAbortController = useRef<AbortController | null>(null)
  if (generateTitlesMutationAbortController.current === null)
    generateTitlesMutationAbortController.current = new AbortController()
  const generateTitlesMutation = useMutation(generateTitles)

  const generateIntroductionMutationAbortController = useRef<AbortController | null>(null)
  if (generateIntroductionMutationAbortController.current === null)
    generateIntroductionMutationAbortController.current = new AbortController()
  const generateIntroductionMutation = useMutation(generateIntroduction)

  const generateOutlineMutationAbortController = useRef<AbortController | null>(null)
  if (generateOutlineMutationAbortController.current === null)
    generateOutlineMutationAbortController.current = new AbortController()
  const generateOutlineMutation = useMutation(generateOutline)

  const { generateContent, generateDocMutation } = useGenerateContent({
    generationType: ChannelType.BlogPost,
    articleId,
    layerId,
    nodeSelection,
    promptLibrary: seasonalPromotion === 'black_friday' ? 'black_friday' : 'default',
    isTurnInto,
    onSuccess,
  })

  const outlineEditor = useRef<OutlineEditorHandle | null>(null)

  const addTitleOption = () => {
    setTitleOptions((oldTitleOptions) => {
      return [...oldTitleOptions, [ourUuid(), '']]
    })
  }

  const updateTitleOption = (newTitleOption: Identifiable<string>) => {
    setTitleOptions((oldTitleOptions) => {
      return oldTitleOptions.map((t) => {
        if (t[0] === newTitleOption[0]) return newTitleOption
        return t
      })
    })
  }

  const generateTitleOptions = async (payload: Values) => {
    setTitleOptions([])
    const prompt: GeneratePrompt = { ...payload, generation_type: ChannelType.BlogPost }
    generateTitlesMutation.mutate(
      {
        payload: {
          prompt,
          workspaceId: workspaceId,
        },
        signal: generateTitlesMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          const titleOptions: Array<Identifiable<string>> = data.map((t) => [ourUuid(), t])
          setTitleOptions(titleOptions)
          setSelectedTitleId(titleOptions[0][0])
        },
        onError(error) {
          reportError(error)
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
              goToPreviousStep()
            },
            onTryAgain: () => {
              closeModal()
              generateTitleOptions(payload)
            },
          })
        },
      },
    )
  }

  const generateIntro = async () => {
    setIntroduction('')
    const prompt: GeneratePrompt = { ...getPayload(), generation_type: ChannelType.BlogPost }
    generateIntroductionMutation.mutate(
      {
        payload: {
          prompt,
          workspaceId: workspaceId,
          title,
        },
        signal: generateIntroductionMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          setIntroduction(data ?? '')
        },
        onError(error) {
          reportError(error)
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
              setCurrentStep(2)
            },
            onTryAgain: () => {
              closeModal()
              generateIntro()
            },
          })
        },
      },
    )
  }

  const generateHeaders = async () => {
    outlineEditor.current?.clear()
    setOutline('')
    const prompt: GeneratePrompt = { ...getPayload(), generation_type: ChannelType.BlogPost }
    generateOutlineMutation.mutate(
      {
        payload: {
          prompt,
          workspaceId: workspaceId,
          title,
        },
        signal: generateOutlineMutationAbortController.current?.signal,
      },
      {
        onSuccess: async (data) => {
          setOutline(data)
          outlineEditor.current?.replaceHTMLString(data)
        },
        onError(error) {
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
              setCurrentStep(2)
            },
            onTryAgain: () => {
              closeModal()
              generateHeaders()
            },
          })
          reportError(error)
        },
      },
    )
  }

  const submitStepTwo = () => {
    setCurrentStep(3)
    generateIntro()
    generateHeaders()
  }

  const sourceMaterials = useSourceMaterials()
  const getPayload = (): Partial<GeneratePrompt & { filename?: string; project?: AlmanacFile }> => {
    const referenceText = (
      formValues.source_materials
        ? sourceMaterials?.data?.filter(({ id }) => formValues.source_materials?.includes(id)) || []
        : []
    )
      .map(({ content }) => content)
      .join('\n')

    const fields = {
      generation_type: ChannelType.BlogPost,
      title,
      introduction,
      ...formValues,
      reference_text: referenceText.length > 0 ? referenceText : undefined,
    }
    return {
      project: formValues.project,
      ...fields,
      format: outline,
    }
  }

  const submitStepThree = async (
    additionalPayload?: Partial<
      GeneratePrompt & { filename?: string; project?: AlmanacFile; articleId?: string | null }
    >,
  ) => {
    onboarding.markCTAAsCompleted(CTA.BlogPost)
    let payload = getPayload()
    return generateContent({ ...payload, ...additionalPayload })
  }

  const goToPreviousStep = () => setCurrentStep((s) => s - 1)

  return (
    <WizardProvider schema={schema}>
      <Modal.Root>
        <ResponsiveDesign.Desktop>
          <Header
            steps={
              isTabletPortrait
                ? ['Goals', 'Title', 'Outline', 'Doc']
                : ['Purpose & Goals', 'Blog Title', 'Blog Outline', 'Generate Doc']
            }
            currentStep={currentStep}
            onStepPress={setCurrentStep}
            stepperVariant={seasonalPromotion === 'black_friday' ? 'yellow' : 'blue'}
            title="Blog Post"
            icon={Blog}
            previewUrl="https://res.cloudinary.com/almanac/image/upload/v1710183787/Home%20Card%20Previews/xfpedur3adlb5odloqfm.png"
          />
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <MobileHeader
            steps={['Goals', 'Title', 'Outline', 'Doc']}
            currentStep={currentStep}
            onStepPress={setCurrentStep}
            title="Blog Post"
            icon={Blog}
          />
        </ResponsiveDesign.Mobile>
        {currentStep === 1 && (
          <StepOne
            schema={schema}
            articleId={articleId}
            values={formValues}
            seasonalPromotion={seasonalPromotion}
            onSubmit={(values) => {
              setCurrentStep(2)
              setFormValues(values)
              generateTitleOptions(values)
            }}
          />
        )}

        {currentStep === 2 && (
          <Form.Root>
            <Form.Section>
              <Form.SectionContent>
                <Form.Fields>
                  <Heading level={4} id="wizard-header">
                    Pick a title for your post, or write your own
                  </Heading>
                  {generateTitlesMutation.isLoading && <TitlesLoadingState />}
                  {!generateTitlesMutation.isLoading && (
                    <RadioGroup
                      aria-label="Title"
                      value={selectedTitleId}
                      className={Styles.radioGroup}
                      onChange={(selectedUuid) => {
                        setSelectedTitleId(selectedUuid)
                      }}
                    >
                      {titleOptions.map((titleOption, i) => {
                        const [uuid, value] = titleOption
                        return (
                          <Radio className={Styles.titleOption} value={uuid} aria-label={value} key={uuid} hideRadio>
                            {(radioBulletProps) => (
                              <>
                                <div />
                                <div className={Styles.inputLabel}>Title #{i + 1}</div>
                                <div>
                                  <RadioBullet {...radioBulletProps} size={20} />
                                </div>
                                <div>
                                  <Input
                                    isTextarea
                                    scrollable={false}
                                    className={Styles.boldInput}
                                    size="lg"
                                    name={`Title ${i + 1}`}
                                    value={value}
                                    onChange={(e) => updateTitleOption([uuid, e.target.value])}
                                    autoFocus={value === ''}
                                    aria-label="Generated Title"
                                    placeholder="Write your own title..."
                                    onKeyDown={(e) => {
                                      // Prevent up and down from selecting other radio options
                                      e.stopPropagation()
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </Radio>
                        )
                      })}
                    </RadioGroup>
                  )}
                  <div className={Styles.titleOptionsFieldsActions}>
                    <Button
                      variant="tertiary"
                      size="lg"
                      frontIcon={Edit}
                      onPress={() => addTitleOption()}
                      isDisabled={generateTitlesMutation.isLoading}
                    >
                      Write Your Own
                    </Button>
                  </div>

                  {formValues.seo_words.length > 0 && (
                    <div className={Styles.titleOptionsFieldsFooter}>
                      <div className={Styles.inputLabel}>Your SEO keywords we tried to include in the above titles</div>
                      <div className={Styles.seoWordsChips}>
                        {formValues.seo_words.map((seoWord, i) => (
                          <span key={`seoWord-${i}`} className={Styles.seoWordChip}>
                            {seoWord}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Form.Fields>
              </Form.SectionContent>
            </Form.Section>
            <Form.Actions isSticky>
              {!isMobile && (
                <Form.PreviousButton
                  onPress={() => {
                    generateTitlesMutationAbortController.current?.abort()
                    generateTitlesMutationAbortController.current = new AbortController()
                    goToPreviousStep()
                  }}
                >
                  Previous Step
                </Form.PreviousButton>
              )}

              <Form.PrimaryActions>
                <Button
                  variant="tertiary"
                  size="lg"
                  onPress={() => generateTitleOptions(formValues)}
                  isDisabled={generateTitlesMutation.isLoading}
                  fullWidth={isMobile}
                >
                  Generate New Titles
                </Button>
                <Button
                  size="lg"
                  onPress={() => submitStepTwo()}
                  endIcon={ArrowRight}
                  isDisabled={generateTitlesMutation.isLoading}
                  fullWidth={isMobile}
                >
                  Generate Outline
                </Button>
              </Form.PrimaryActions>
            </Form.Actions>
          </Form.Root>
        )}

        {currentStep === 3 && (
          <Form.Root>
            <Form.Section>
              <Form.SectionContent>
                <Form.Fields className={Styles.fieldsWrapper}>
                  <Heading level={4} id="wizard-header">
                    Edit your outline as desired
                  </Heading>
                  <Field>
                    <div className={Styles.inputField}>
                      <div className={Styles.inputLabel}>Title</div>
                      <Input
                        isTextarea
                        scrollable={false}
                        className={Styles.boldInput}
                        name="title"
                        value={title}
                        onChange={(e) => {
                          if (!selectedTitleId) return
                          updateTitleOption([selectedTitleId, e.target.value])
                        }}
                      />
                    </div>

                    <div className={Styles.inputField}>
                      <div className={Styles.inputLabel}>Introduction</div>
                      {generateIntroductionMutation.isLoading && <TextFieldLoadingState />}
                      {generateIntroductionMutation.isSuccess && (
                        <>
                          <Input
                            isTextarea
                            scrollable={false}
                            rows={4}
                            name="introduction"
                            value={introduction}
                            onChange={(e) => setIntroduction(e.target.value)}
                          />
                          <div className={Styles.inputActions}>
                            <Button variant="tertiary" size="lg" frontIcon={AI} onPress={() => generateIntro()}>
                              Regenerate Intro
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Field>

                  <Field
                    label="This is your blog's outline, with H2s and H3s for paragraph headers."
                    description={
                      <>
                        Press <kbd>enter</kbd> to add new H2s and H3s and <kbd>tab</kbd> <kbd>shift + tab</kbd> to
                        indent / unindent lines.
                      </>
                    }
                  >
                    {generateOutlineMutation.isLoading && <OutlineLoadingState />}
                    {generateOutlineMutation.isSuccess && (
                      <>
                        <OutlineEditor
                          ref={outlineEditor}
                          aria-label="Outline"
                          className={Styles.outlineEditor}
                          initialHtmlString={outline}
                          onChange={setOutline}
                        />

                        <div className={Styles.inputActions}>
                          <Button variant="tertiary" size="lg" frontIcon={AI} onPress={() => generateHeaders()}>
                            Regenerate Headers
                          </Button>
                        </div>
                      </>
                    )}
                  </Field>
                </Form.Fields>
              </Form.SectionContent>
            </Form.Section>
            <Form.Actions isSticky>
              {!isTabletPortrait && (
                <Form.PreviousButton
                  onPress={() => {
                    generateIntroductionMutationAbortController.current?.abort()
                    generateIntroductionMutationAbortController.current = new AbortController()
                    generateOutlineMutationAbortController.current?.abort()
                    generateOutlineMutationAbortController.current = new AbortController()
                    goToPreviousStep()
                  }}
                >
                  Previous Step
                </Form.PreviousButton>
              )}

              <Form.PrimaryActions>
                {children ? (
                  children({
                    submitForm: submitStepThree,
                    isSubmitting:
                      generateOutlineMutation.isLoading ||
                      generateIntroductionMutation.isLoading ||
                      generateDocMutation.isLoading,
                  })
                ) : (
                  <Button
                    size="lg"
                    endIcon={ArrowRight}
                    onPress={() => submitStepThree()}
                    isDisabled={
                      generateOutlineMutation.isLoading ||
                      generateIntroductionMutation.isLoading ||
                      generateDocMutation.isLoading
                    }
                    fullWidth={isMobile}
                  >
                    Generate Post
                  </Button>
                )}
              </Form.PrimaryActions>
            </Form.Actions>
          </Form.Root>
        )}
      </Modal.Root>
    </WizardProvider>
  )
}

export { BlogPostWizard }
